import React, { useState, forwardRef } from "react";
import { colors } from "constants/colors";
import styled, { css } from "styled-components";
import { StyledText } from "styles/Text";
import { ProductListModel } from "models/productListModel";
import { API_IMAGE_CONTEXT } from "constants/api";
import defaultImg from "assets/images/store/img_thumbnail_store.png";
import { formatNumber } from "util/numbers";
import { getKeywordIcon } from "util/benefit";

interface Props {
  productData: ProductListModel;
  onClickProduct?: () => void;
}

const ProductListItemHorizontal = forwardRef<HTMLLIElement, Props>(
  ({ productData, onClickProduct }, ref) => {
    const [loaded, setLoaded] = useState(false);
    const discountNum = !!productData?.discountRate
      ? Math.abs(productData.discountRate)
      : 0;
    const discountInfo = {
      discountPrice: productData?.productPrice || 0,
      discountImg: !productData?.discountRate
        ? undefined
        : productData.discountRate < 0
          ? require("assets/images/store/ic_discount_down.png")
          : require("assets/images/store/ic_discount_up.png"),
      isDiscount: !!productData?.discountRate && productData?.discountRate < 0,
    };

    const onClickItem = (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      onClickProduct && onClickProduct();
    };

    return (
      <StyledProductBox key={productData.productNm} ref={ref}>
        <StyledLink onClick={onClickItem}>
          <StyledImgBox>
            {!loaded && (
              <StyledProductImage
                src={require("assets/images/store/img_thumbnail_store.png")}
                alt=""
              />
            )}

            <StyledProductImage
              $prodImg={true}
              $loaded={loaded}
              src={
                productData.productImage?.startsWith("/")
                  ? `${API_IMAGE_CONTEXT}${productData.productImage}`
                  : productData.productImage
              }
              alt={productData.productNm}
              onLoad={() => setLoaded(true)}
              onError={(e) => (e.currentTarget.src = defaultImg)}
            />
            {!!productData.isAd && <StyledAd>AD</StyledAd>}
          </StyledImgBox>
          <StyledProductInfoBox>
            {!!productData.deliveryNm && (
              <StyledKeyword>
                <StyledIcon
                  src={getKeywordIcon(productData.deliveryNm)}
                  width={16}
                  height={16}
                />
                {productData.deliveryNm}
              </StyledKeyword>
            )}
            <StyledProductTitleTextBox>
              {productData.productNm}
            </StyledProductTitleTextBox>
            <StyledProductPriceBox>
              <StyledText.Headline17>
                {productData.productPrice &&
                  formatNumber(discountInfo.discountPrice)}
                원
              </StyledText.Headline17>
              {!!discountNum && (
                <StyledDiscountText $isDiscount={discountInfo.isDiscount}>
                  {Math.abs(discountNum)}%
                  <StyledDiscountImage
                    src={discountInfo.discountImg}
                    width={10}
                    height={10}
                  />
                </StyledDiscountText>
              )}
            </StyledProductPriceBox>
            {!!productData?.dayDiff && (
              <StyledText.Sub12 color={colors.darkGray1}>
                {formatNumber(productData.previousprice || 0)}원{" "}
                {productData.dayDiff}
              </StyledText.Sub12>
            )}
          </StyledProductInfoBox>
        </StyledLink>
      </StyledProductBox>
    );
  },
);

export default ProductListItemHorizontal;

const StyledProductBox = styled.li``;

const StyledImgBox = styled.div`
  overflow: hidden;
  display: flex;
  position: relative;
  z-index: -1;
  width: 40%;
  max-width: 160px;
  border-radius: 12px;
  aspect-ratio: 1 / 1;
  border: 1px solid ${colors.lightGray1};
  margin-right: 16px;
`;

const StyledProductImage = styled.img<{
  $loaded?: boolean;
  $prodImg?: boolean;
}>`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;

  ${({ $prodImg, $loaded }) =>
    $prodImg &&
    css`
      opacity: ${$loaded ? 1 : 0};
      transition: opacity 0.3s ease;
    `}
`;

const StyledAd = styled.span`
  padding: 1px 4px;
  border-radius: 16px;
  font-size: 9px;
  line-height: normal;
  color: ${colors.white};
  background: rgba(102, 102, 102, 0.2);
  position: absolute;
  right: 8px;
  bottom: 8px;
  z-index: 1;
`;

const StyledProductTitleTextBox = styled.p`
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  font-size: 13px;
  font-style: normal;
  line-height: 20px;
  letter-spacing: -0.65px;
  margin-bottom: 8px;
`;

const StyledProductPriceBox = styled.div`
  margin-bottom: 2px;
`;

const StyledDiscountText = styled.span<{ $isDiscount?: boolean }>`
  font-size: 14px;
  font-style: normal;
  line-height: 21px;
  letter-spacing: -0.7px;
  color: ${(props) => (props.$isDiscount ? colors.statusError : "#5186CC")};
  display: inline-flex;
  align-items: center;
  margin-left: 4px;
`;

const StyledDiscountImage = styled.img``;

const StyledProductInfoBox = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const StyledKeyword = styled.p`
  margin-bottom: 8px;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 18px;
  letter-spacing: -0.6px;
`;

const StyledLink = styled.a`
  display: flex;
`;

const StyledIcon = styled.img``;
